import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import BreathSplashScreen from '@/components/BreathSplashScreen';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, loginAsGuest } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  
  // Check session storage synchronously to determine initial state
  const hasSeenSplashInitially = sessionStorage.getItem('hasSeenLoginSplash') === 'true';
  const [showSplash, setShowSplash] = useState(!hasSeenSplashInitially);
  const [loginFormOpacity, setLoginFormOpacity] = useState(hasSeenSplashInitially ? 1 : 0);
  const [isReady, setIsReady] = useState(hasSeenSplashInitially);

  // Handle splash completion - fade in login form
  const handleSplashComplete = () => {
    setShowSplash(false);
    setIsReady(true);
    // Small delay then fade in the login form
    setTimeout(() => {
      setLoginFormOpacity(1);
    }, 50);
  };

  // Mark splash as seen when it completes
  useEffect(() => {
    if (!showSplash && isReady) {
      sessionStorage.setItem('hasSeenLoginSplash', 'true');
    }
  }, [showSplash, isReady]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Login failed';
      
      // Handle Firebase auth errors
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      await loginAsGuest();
      toast.success('Welcome! Continuing as guest');
      navigate('/dashboard');
    } catch (error) {
      console.error('Guest login error:', error);
      toast.error('Guest login failed. Please try again.');
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <>
      {/* Splash background overlay - prevents flash before splash loads */}
      {showSplash && (
        <div 
          className="fixed inset-0 z-[99]" 
          style={{ background: '#2c4240' }} 
        />
      )}
      
      {/* Breath Splash Screen - shown on every login visit */}
      {showSplash && (
        <BreathSplashScreen onComplete={handleSplashComplete} />
      )}
      
      {/* Login Form - fades in after splash */}
      <div 
        className="min-h-screen flex flex-col relative"
        style={{ 
          opacity: loginFormOpacity,
          transition: 'opacity 500ms ease-in-out',
          display: showSplash ? 'none' : 'flex'
        }}
      >
        {/* Main content area - centered card */}
        <div className="flex-1 flex items-center justify-center p-4 px-4 sm:px-6 md:px-8">
        <Card className="w-full max-w-md mx-auto" style={{ background: 'var(--bg-card)', backdropFilter: 'blur(10px)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
        <CardHeader className="space-y-2 text-center">
          <h1 className="text-4xl font-bold" 
              style={{ 
                fontFamily: 'Work Sans', 
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
            CalmGuard
          </h1>
          <p className="text-base mb-16" style={{ color: '#4b5563', fontFamily: 'Work Sans' }}>
            Tools for Navigating Narcissism
          </p>
          <div className="inline-flex items-center justify-center rounded-full mx-auto mb-4 mt-12 overflow-hidden" 
               style={{ background: 'transparent', width: '120px', height: '120px' }}>
            <img src="/logo192.png" alt="CalmGuard Logo" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
          </div>
          <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Work Sans', color: 'var(--text-primary)' }}>
            Welcome Back
          </CardTitle>
          <CardDescription style={{ color: 'var(--text-secondary)' }}>
            Sign in to access your CalmGuard plans
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: 'var(--text-primary)' }}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading || guestLoading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" style={{ color: 'var(--text-primary)' }}>Password</Label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm hover:underline"
                  style={{ color: 'var(--primary-blue)' }}
                >
                  Forgot Password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || guestLoading}
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11"
              disabled={loading || guestLoading}
              style={{ 
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                color: 'white'
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" style={{ borderColor: 'var(--border)' }} />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2" style={{ background: 'var(--bg-card)', color: 'var(--text-secondary)' }}>
                Or
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full h-11"
            onClick={handleGuestLogin}
            disabled={loading || guestLoading}
          >
            {guestLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Continue as Guest'
            )}
          </Button>

          <div className="text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
            Don&apos;t have an account?{' '}
            <Link 
              to="/register" 
              className="font-medium hover:underline"
              style={{ color: 'var(--primary-blue)' }}
            >
              Sign up
            </Link>
          </div>

          <p className="text-xs text-center" style={{ color: 'var(--text-secondary)' }}>
            Guest mode allows you to try the app without creating an account. 
            Your data will be saved but won&apos;t sync across devices.
          </p>
        </CardContent>
      </Card>
      </div>
      
      {/* Footer link - outside auth container, anchored at screen bottom */}
      <div className="pt-6 pb-16 text-center">
        <Link 
          to="/about" 
          className="text-xs hover:underline"
          style={{ color: 'var(--text-secondary)', opacity: 0.6 }}
          data-testid="about-calmguard-link"
        >
          About CalmGuard
        </Link>
      </div>
      </div>
    </>
  );
}
